/* eslint-disable react/jsx-one-expression-per-line */
import React, {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { makeReduxLoginAuth } from 'main/factories/usecases/auth/LoginAuthFactory';
import { makeReduxCloseModal } from 'main/factories/usecases/modal/CloseModalFactory';

import { IconCodata, IconEmail, IconPadlock } from 'presentation/base/icons';

import { ChangeRoles } from 'presentation/components/changeRoles';
import { Button, Input, Modal } from 'presentation/components/UI';

import { ThemeContext } from 'App';
import { iStore } from 'domain/interfaces/models';
import { makeRemoteGetInfoAuth } from 'main/factories/usecases/auth/GetInfoAuthFactory.ts';
import { translator } from 'presentation/components/i18n';
import Translator from 'presentation/components/i18n/Translator';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  Body,
  ButtonsContainer,
  Container,
  FlexContainer,
  FooterLeft,
  FooterRight,
  Forgotpass,
  Form,
  Left,
  OrLoginOption,
  RemindMe,
  Right,
  Title,
  WelcomeTo,
} from './styles/styledLogin';

const Login: React.FC = (): JSX.Element => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authURL, setAuthURL] = useState('');

  const { loading } = useSelector((store: iStore) => store.auth);

  const { push } = useHistory();
  const { theme } = useContext(ThemeContext);

  const handleLogin = useCallback(
    // eslint-disable-next-line consistent-return
    (e: FormEvent) => {
      e.preventDefault();

      if (!loading) {
        if (email === '' || password === '') {
          return toast.error('Preencha todos os campos.');
        }

        makeReduxLoginAuth().login({
          body: {
            login: email,
            password,
          },
        });
      }
    },
    [email, loading, password],
  );

  const handleCodataRedirect = useCallback(() => {
    window.location.href = authURL;
  }, [authURL]);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hashParams = new URLSearchParams(
      currentUrl.hash.startsWith('#')
        ? currentUrl.hash.slice(1)
        : currentUrl.hash,
    );
    const code = currentUrl.searchParams.get('code') ?? hashParams.get('code');

    if (code) {
      makeReduxLoginAuth().login({
        body: {
          authMode: 'EXTERNAL',
          identityCode: code,
        },
      });
    }

    makeRemoteGetInfoAuth()
      .get({})
      .then(
        res => {
          if (res.authMode === 'EXTERNAL' && res.authentication.authURL) {
            setAuthURL(res.authentication.authURL);
          }
        },
        err => {
          console.log('err: ', err);
        },
      );
  }, []);

  return (
    <Container
      banner={
        theme.images?.banner ??
        'https://minio.v4h.cloud/agenda/v4h_agenda_banner.svg'
      }
    >
      <Body>
        <Left>
          {theme.images.logo.main && (
            <img
              src={theme.images.logo.main}
              alt="Logo"
              width="200px"
              height="40px"
            />
          )}
          <Form id="loginAuthentication" onSubmit={handleLogin}>
            <Title>{translator('Fazer Login')}</Title>
            <Input
              data-testid="input-email"
              id="loginInput"
              name="login"
              type="text"
              label={translator('E-mail')}
              icon={IconEmail}
              placeholder={translator('Digite o email cadastrado')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
            />

            <Input
              data-testid="input-password"
              id="loginPassword"
              type="password"
              name="password"
              label={translator('Senha')}
              icon={IconPadlock}
              placeholder={translator('Digite sua senha')}
              value={password}
              onChange={e => setPassword(e.target.value)}
            />

            <FlexContainer>
              <RemindMe>
                <input type="checkbox" />
                <span>{translator('Lembrar-me')}</span>
              </RemindMe>
              <Forgotpass
                data-testid="btn-forgot"
                id="loginForgotPass"
                href="/pwd/forgot"
              >
                {translator('Esqueceu sua senha?')}
              </Forgotpass>
            </FlexContainer>

            <ButtonsContainer>
              <Button
                height="48px"
                data-testid="btn-login"
                id="loginSubmit"
                type="submit"
                disabled={!password || !email || loading}
              >
                {loading
                  ? `${translator('Carregando...')}`
                  : translator('Entrar')}
              </Button>
              {authURL && (
                <>
                  <div className="login-type">
                    <div className="gray-line" />
                    <OrLoginOption>{Translator('ou')}</OrLoginOption>
                    <div className="gray-line" />
                  </div>
                  <Button
                    height="50px"
                    data-testid="btn-codata-login"
                    background="transparent"
                    color="#747474"
                    style={{ border: '1px solid #747474' }}
                    icon={IconCodata}
                    onClick={() => handleCodataRedirect()}
                  >
                    Autenticar com a CODATA
                  </Button>
                </>
              )}
            </ButtonsContainer>
          </Form>
          <FooterLeft>
            <a
              href="https://app.clickup.com/3025146/v/dc/2wa7u-4173/2wa7u-3293"
              target="_blank"
              rel="noreferrer"
            >
              {translator('Versão')} 2.59.0
            </a>
            <p>
              © {theme.systemName}.
              {translator(' Todos os direitos reservados.')}
            </p>
          </FooterLeft>
        </Left>
        <Right>
          <img
            src={theme.images.favicon}
            alt="Favicon"
            width="120px"
            height="80px"
          />
          <WelcomeTo>
            {translator('Seja bem vindo ao')}
            <h1>{theme.systemName ?? theme.emailParams.systemName}</h1>
          </WelcomeTo>
          <div className="middle-line" />
          {theme.texts?.banner ? (
            <span
              dangerouslySetInnerHTML={{
                __html: theme.texts.banner,
              }}
            />
          ) : (
            <>
              <span>Agende</span> reuniões e eventos com <span>facilidade</span>{' '}
              e <span>segurança</span>!
            </>
          )}
          <FooterRight>
            <span>{translator('Consulte também os')}</span>
            <p>
              <a
                href={theme.emailParams.serviceTerms}
                target="_blank"
                rel="noreferrer"
              >
                {translator('Termos de Serviço')}
              </a>
              <span>{translator('e')}</span>
              <a
                href={theme.emailParams.serviceTerms}
                target="_blank"
                rel="noreferrer"
              >
                {translator('Políticas de Privacidade')}
              </a>
            </p>
          </FooterRight>
        </Right>
      </Body>
      <Modal
        title={Translator('Seleção de papeis')}
        handleClose={() => makeReduxCloseModal().close({})}
      >
        <ChangeRoles />
      </Modal>
    </Container>
  );
};

export default Login;
